import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { TrendingUp, Shield, Zap, ArrowRight, Building2, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';

export const Landing = () => {
  const navigate = useNavigate();
  const { isConnected, connectWallet } = useWeb3();

  const handleGetStarted = async () => {
    if (!isConnected) {
      await connectWallet();
    }
    navigate('/marketplace');
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      
      <section data-testid="hero-section" className="relative overflow-hidden py-24 md:py-32">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1639322537231-2f206e06af84?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1
              data-testid="hero-title"
              className="text-5xl md:text-7xl font-black tracking-tighter mb-6 gradient-text"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Decentralized Invoice Financing
            </h1>
            <p data-testid="hero-subtitle" className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              Unlock instant liquidity for your invoices. Earn high yields by financing verified business invoices on the blockchain.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="get-started-btn"
                onClick={handleGetStarted}
                size="lg"
                className="gradient-bg text-white hover:opacity-90 h-14 px-10 text-base font-semibold button-scale shadow-lg shadow-indigo-500/30"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="msme-dashboard-btn"
                onClick={() => navigate('/msme')}
                size="lg"
                variant="outline"
                className="h-14 px-10 text-base font-semibold border-2 border-slate-300 hover:border-indigo-600 hover:text-indigo-600"
              >
                MSME Dashboard
                <Building2 className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="features-section" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              data-testid="feature-liquidity"
              className="professional-card p-8 hover:border-indigo-600"
            >
              <div className="h-12 w-12 gradient-bg rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Instant Liquidity
              </h3>
              <p className="text-slate-600 leading-relaxed">
                MSMEs get immediate payment for their invoices, improving cash flow without waiting for due dates.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-testid="feature-yields"
              className="professional-card p-8 hover:border-indigo-600"
            >
              <div className="h-12 w-12 gradient-bg rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                High Yields
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Investors earn 15-25% APY by financing invoices, with risk-adjusted returns based on credit scores.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              data-testid="feature-security"
              className="professional-card p-8 hover:border-indigo-600"
            >
              <div className="h-12 w-12 gradient-bg rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Blockchain Security
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Smart contracts ensure transparent, trustless transactions with automatic escrow and settlement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="how-it-works-section" className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              How It Works
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Simple, transparent process for both MSMEs and investors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div data-testid="msme-flow" className="space-y-6">
              <h3 className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                For MSMEs
              </h3>
              <div className="space-y-4">
                {[
                  { step: 1, text: 'Submit your invoice - System verifies and mints it as a secure Invoice NFT' },
                  { step: 2, text: 'NFT is automatically listed on the P2P marketplace' },
                  { step: 3, text: 'Investors fund your invoice - Get instant cash (97% of invoice value)' },
                  { step: 4, text: 'Smart contracts automate repayment when debtor pays on due date' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full gradient-bg flex items-center justify-center font-bold text-white flex-shrink-0 shadow-lg shadow-indigo-500/30">
                      {item.step}
                    </div>
                    <p className="text-slate-600 leading-relaxed pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="investor-flow" className="space-y-6">
              <h3 className="text-2xl font-bold text-indigo-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                For Investors
              </h3>
              <div className="space-y-4">
                {[
                  { step: 1, text: 'Browse P2P marketplace of verified Invoice NFTs' },
                  { step: 2, text: 'Select invoices by risk score and expected yield (15-25% APY)' },
                  { step: 3, text: 'Fund invoice with one transaction - MSME gets instant cash' },
                  { step: 4, text: 'Smart contracts automatically distribute principal + yield on due date' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full gradient-bg flex items-center justify-center font-bold text-white flex-shrink-0 shadow-lg shadow-indigo-500/30">
                      {item.step}
                    </div>
                    <p className="text-slate-600 leading-relaxed pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Ready to Get Started?
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Connect your wallet and explore the marketplace of verified business invoices.
            </p>
            <Button
              data-testid="cta-connect-wallet"
              onClick={handleGetStarted}
              size="lg"
              className="gradient-bg text-white hover:opacity-90 h-14 px-10 text-base font-semibold button-scale shadow-lg shadow-indigo-500/30"
            >
              <Wallet className="mr-2 h-5 w-5" />
              {isConnected ? 'Browse Marketplace' : 'Connect Wallet'}
            </Button>
          </div>
        </div>
      </section>

      <footer data-testid="footer" className="py-12 border-t border-slate-200 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center text-sm text-slate-500">
            <p>© 2025 ChainVoice. Decentralized Invoice Financing on Sepolia Testnet.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};